import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import { ChevronDown, ChevronRight, RefreshCw, Loader2, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { initializeZoneDMApi } from '../services/zonedm';
import { GHL_AUTH_MISSING_MESSAGE, resolveGhlAuth, toZoneDmCredentials } from '../services/ghlAuth';
import { generateDefaultPipelineMappings, checkNeedsDefaultMappings } from '../services/pipelineTemplates';

type StateCombination = {
  appointment_type_external_id: number | null;
  appointment_type_name: string;
  procedure_external_id: number | null;
  procedure_name: string | null;
  appointment_status: string;
  patient_status: string | null;
  count: number;
  unique_patients: number;
};

type PipelineMapping = {
  appointment_type_external_id: number | null;
  procedure_external_id: number | null;
  appointment_status: string | null;
  patient_status: string | null;
  hl_pipeline_id: string | null;
  hl_pipeline_stage_id: string | null;
  opportunity_status: string | null;
  skip_opportunity: boolean;
  /** true = always create new; false = reuse an open card and create if none exists */
  create_new_opportunity: boolean;
  date_field_mode: DateFieldMode;
  stage_order: number;
  priority_order: number;
};

type DateFieldMode = 'auto' | 'consult_booked' | 'procedure_booked' | 'none';

type CoverageRow = {
  externalId: number;
  name: string;
  appointmentCount: number;
  status: 'red' | 'yellow' | 'green';
  reason: string;
};

function isProtectedVisitingSurgeonType(
  name?: string | null,
  externalId?: number | null,
  userId?: string | null,
): boolean {
  if (
    userId === '775bc835-cf74-48ba-ab54-252d7a2581af' &&
    (Number(externalId) === 14 || Number(externalId) === 23)
  ) return true;
  return String(name || '').trim().toLowerCase().includes('visiting surgeon');
}

const DATE_FIELD_MODE_OPTIONS: Array<{ value: DateFieldMode; label: string }> = [
  { value: 'auto', label: 'Auto (By Stage)' },
  { value: 'consult_booked', label: 'Consultation Date' },
  { value: 'procedure_booked', label: 'Procedure Date' },
  { value: 'none', label: 'Do Not Set Date' },
];

function normalizeDateFieldMode(value: unknown): DateFieldMode {
  if (typeof value !== 'string') return 'auto';
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'auto' ||
    normalized === 'consult_booked' ||
    normalized === 'procedure_booked' ||
    normalized === 'none'
  ) {
    return normalized;
  }
  return 'auto';
}

// Pipeline type is inferred from hlPipelines query

// Valid appointment_status enum values from database
// All 4D status codes: Unconfirmed=1, LeftMessage=2, Confirmed=3, Arrived=4, Roomed=5,
// Seen=6, CheckedOut=7, LeftUnseen=8, NoShow=9, Happening=10,
// RescheduledByOffice=11, RescheduledByPatient=12,
// CancelledByOffice=13, CancelledByPatient=14, RunningLate=17
const ALL_APPOINTMENT_STATUSES = [
  'unconfirmed',
  'left_message',
  'confirmed',
  'arrived',
  'roomed',
  'seen',
  'checked_out',
  'left_unseen',
  'no_show',
  'happening',
  'rescheduled_by_office',
  'rescheduled_by_patient',
  'cancelled_by_office',
  'cancelled_by_patient',
  'running_late',
];

export default function PipelineMapping() {
  const queryClient = useQueryClient();
  const [dateFilter, setDateFilter] = React.useState<'current_year' | 'all'>('current_year');
  const [collapsed, setCollapsed] = React.useState(true);
  const [editableMappings, setEditableMappings] = React.useState<Record<string, PipelineMapping>>({});
  const [selectedPipeline, setSelectedPipeline] = React.useState<Record<string, string>>({});
  const [selectedStage, setSelectedStage] = React.useState<Record<string, string>>({});
  const [selectedOppStatus, setSelectedOppStatus] = React.useState<Record<string, string>>({});
  const [priorityOrders, setPriorityOrders] = React.useState<Record<string, number>>({});
  const [selectedStatuses, setSelectedStatuses] = React.useState<Record<string, string[]>>({});
  const [createNewOpp, setCreateNewOpp] = React.useState<Record<string, boolean>>({});
  const [dateFieldModes, setDateFieldModes] = React.useState<Record<string, DateFieldMode>>({});
  const [stageOrders, setStageOrders] = React.useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [isGeneratingDefaults, setIsGeneratingDefaults] = React.useState(false);
  const [showAutoMapPrompt, setShowAutoMapPrompt] = React.useState(false);

  // Get ZoneDM config
  const { data: zoneDMConfig } = useQuery({
    queryKey: ['zonedm-config'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('integrations')
        .select('user_id, access_token, location_id, config')
        .eq('type', 'zonedm')
        .eq('user_id', session.user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentLocationId = (zoneDMConfig as any)?.config?.locationId || (zoneDMConfig as any)?.location_id || null;
  const currentUserId = (zoneDMConfig as any)?.user_id || null;

  // Fetch pipelines from ZoneDM (scoped to current location)
  const { data: hlPipelines } = useQuery({
    queryKey: ['hl_pipelines', currentLocationId],
    enabled: !!currentLocationId,
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      if (!currentLocationId) return [];
      const { data, error } = await supabase
        .from('hl_pipelines')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('location_id', currentLocationId)
        .order('name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch existing mappings
  const { data: catalogTypes } = useQuery({
    queryKey: ['appointment_types_catalog'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('appointment_types')
        .select('id, external_id, name, sync_status, is_active')
        .eq('user_id', session.user.id)
        .order('external_id');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: typeAlerts } = useQuery({
    queryKey: ['appointment_type_alerts'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('appointment_type_alerts')
        .select('appointment_type_external_id, appointment_type_name, delivery_status')
        .eq('user_id', session.user.id);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: existingMappings } = useQuery({
    queryKey: ['appointment_pipeline_mappings'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const pageSize = 1000;
      const all: any[] = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('appointment_pipeline_mappings')
          .select('*')
          .eq('user_id', session.user.id)
          .order('priority_order', { ascending: false })
          .order('id', { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        all.push(...data);
        if (data.length < pageSize) break;
        from += pageSize;
      }
      return all;
    },
  });

  // Load mappings into editable state
  React.useEffect(() => {
    if (!existingMappings) return;
    const mappings: Record<string, PipelineMapping> = {};
    const pipelines: Record<string, string> = {};
    const stages: Record<string, string> = {};
    const oppStatuses: Record<string, string> = {};
    const priorities: Record<string, number> = {};
    const statusSelections: Record<string, string[]> = {};
    const createNewFlags: Record<string, boolean> = {};
    const dateModes: Record<string, DateFieldMode> = {};
    const stageOrderVals: Record<string, number> = {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    existingMappings.forEach((m: any) => {
      const key = getMappingKey(m);
      mappings[key] = {
        appointment_type_external_id: m.appointment_type_external_id,
        procedure_external_id: m.procedure_external_id,
        appointment_status: m.appointment_status,
        patient_status: m.patient_status,
        hl_pipeline_id: m.hl_pipeline_id,
        hl_pipeline_stage_id: m.hl_pipeline_stage_id,
        opportunity_status: m.opportunity_status,
        skip_opportunity: m.skip_opportunity || false,
        create_new_opportunity: m.create_new_opportunity || false,
        date_field_mode: normalizeDateFieldMode(m.date_field_mode),
        stage_order: m.stage_order || 0,
        priority_order: m.priority_order || 0,
      };
      // Handle empty strings and null values as falsy values
      const hasPipelineId = m.hl_pipeline_id && typeof m.hl_pipeline_id === 'string' && m.hl_pipeline_id.trim() !== '';
      const hasStageId = m.hl_pipeline_stage_id && typeof m.hl_pipeline_stage_id === 'string' && m.hl_pipeline_stage_id.trim() !== '';
      
      if (hasPipelineId) pipelines[key] = m.hl_pipeline_id;
      if (hasStageId) stages[key] = m.hl_pipeline_stage_id;
      if (m.opportunity_status) oppStatuses[key] = m.opportunity_status;
      priorities[key] = m.priority_order || 0;
      createNewFlags[key] = m.create_new_opportunity || false;
      dateModes[key] = normalizeDateFieldMode(m.date_field_mode);
      stageOrderVals[key] = m.stage_order || 0;

      // Track selected statuses per type+patient combo
      // Include status if it has a mapping (pipeline/stage) OR skip_opportunity is true
      const typePatientKey = `${m.appointment_type_external_id}|${m.patient_status || 'null'}`;
      if (m.appointment_status && ((hasPipelineId && hasStageId) || m.skip_opportunity)) {
        if (!statusSelections[typePatientKey]) {
          statusSelections[typePatientKey] = [];
        }
        if (!statusSelections[typePatientKey].includes(m.appointment_status)) {
          statusSelections[typePatientKey].push(m.appointment_status);
        }
      }
    });

    setEditableMappings(mappings);
    setSelectedPipeline(pipelines);
    setSelectedStage(stages);
    setSelectedOppStatus(oppStatuses);
    setPriorityOrders(priorities);
    setSelectedStatuses(statusSelections);
    setCreateNewOpp(createNewFlags);
    setDateFieldModes(dateModes);
    setStageOrders(stageOrderVals);
  }, [existingMappings]);

  // Fetch state combinations
  const { data: stateCombinations, isLoading } = useQuery({
    queryKey: ['pipeline-state-combinations', dateFilter],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const userId = session.user.id;

      // Get ALL appointment types from MASTER DATA (synced daily from 4D metadata)
      // This is the source of truth for available types - NOT derived from appointments
      const { data: allTypes, error: typesError } = await supabase
        .from('appointment_types')
        .select('id, external_id, name')
        .eq('user_id', userId)
        .order('name');
      
      if (typesError) throw typesError;
      
      // Build query with date filter for appointment counts (used for statistics only)
      // Note: Using separate queries to avoid PostgREST join limitations
      let query = supabase
        .from('appointments')
        .select(`
          status,
          patient_id,
          type_id,
          appointment_types!inner(external_id, name)
        `)
        .eq('user_id', userId);

      if (dateFilter === 'current_year') {
        // Get start of current year in UTC
        const year = new Date().getFullYear();
        const yearStart = new Date(Date.UTC(year, 0, 1)).toISOString();
        query = query.gte('start_time', yearStart);
      }

      // Sample appointments for count statistics (the actual types come from master data)
      const { data: appointments, error } = await query.limit(10000);

      if (error) throw error;

      // Continue even if appointments is empty - we still need to show all types with zero counts
      // Fetch patient statuses separately if we have patient IDs
      const patientIdSet = new Set<string>();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      appointments.forEach((a: any) => {
        if (a.patient_id && typeof a.patient_id === 'string') {
          patientIdSet.add(a.patient_id);
        }
      });
      const patientIds: string[] = Array.from(patientIdSet);
      
      const patientStatusMap = new Map<string, string | null>();
      if (patientIds.length > 0) {
        // Batch fetch patient statuses (in chunks to avoid query size limits)
        const chunks: string[][] = [];
        for (let i = 0; i < patientIds.length; i += 500) {
          chunks.push(patientIds.slice(i, i + 500));
        }
        
        for (const chunk of chunks) {
          const { data: patients, error: patErr } = await supabase
            .from('patients')
            .select('id, status')
            .in('id', chunk)
            .eq('user_id', userId);
          
          if (!patErr && patients) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            patients.forEach((p: any) => {
              patientStatusMap.set(p.id, p.status || null);
            });
          }
        }
      }

      // Aggregate distinct combinations
      const combinationMap = new Map<string, StateCombination>();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      appointments?.forEach((appt: any) => {
        const typeExtId = appt.appointment_types?.external_id;
        const typeName = appt.appointment_types?.name || 'Unknown';
        const apptStatus = appt.status;
        const patStatus = appt.patient_id ? (patientStatusMap.get(appt.patient_id) || null) : null;

        const key = `${typeExtId || 'null'}|null|${apptStatus}|${patStatus || 'null'}`;

        if (!combinationMap.has(key)) {
          combinationMap.set(key, {
            appointment_type_external_id: typeExtId,
            appointment_type_name: typeName,
            procedure_external_id: null,
            procedure_name: null,
            appointment_status: apptStatus,
            patient_status: patStatus,
            count: 0,
            unique_patients: 0,
          });
        }

        const combo = combinationMap.get(key)!;
        combo.count++;
        if (appt.patient_id && !combo.unique_patients.toString().includes(appt.patient_id)) {
          combo.unique_patients++;
        }
      });

      // Fix unique_patients count (recalculate properly)
      for (const combo of combinationMap.values()) {
        const uniqueSet = new Set<string>();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        appointments?.forEach((appt: any) => {
          if (
            appt.appointment_types?.external_id === combo.appointment_type_external_id &&
            appt.status === combo.appointment_status &&
            (appt.patient_id ? (patientStatusMap.get(appt.patient_id) || null) : null) === combo.patient_status &&
            appt.patient_id
          ) {
            uniqueSet.add(appt.patient_id);
          }
        });
        combo.unique_patients = uniqueSet.size;
      }

      // Build type+patient status combinations
      // START from MASTER DATA (allTypes) to ensure ALL types are included
      const typePatientCombos = new Map<string, { typeExtId: number; typeName: string; patStatus: string | null }>();
      
      // First: Add ALL types from master data (synced from 4D metadata)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      allTypes?.forEach((type: any) => {
        const tpKey = `${type.external_id}|null`;
        typePatientCombos.set(tpKey, {
          typeExtId: type.external_id,
          typeName: type.name,
          patStatus: null,
        });
      });

      // Then: Add any type+patient_status combinations from actual appointments (for patient status filtering)
      for (const combo of combinationMap.values()) {
        const tpKey = `${combo.appointment_type_external_id}|${combo.patient_status || 'null'}`;
        if (!typePatientCombos.has(tpKey)) {
          typePatientCombos.set(tpKey, {
            typeExtId: combo.appointment_type_external_id!,
            typeName: combo.appointment_type_name,
            patStatus: combo.patient_status,
          });
        }
      }

      // Generate ALL status rows for each type+patient combination
      const allStatusCombinations: StateCombination[] = [];
      for (const { typeExtId, typeName, patStatus } of typePatientCombos.values()) {
        for (const status of ALL_APPOINTMENT_STATUSES) {
          const key = `${typeExtId}|null|${status}|${patStatus || 'null'}`;
          const existing = combinationMap.get(key);

          allStatusCombinations.push({
            appointment_type_external_id: typeExtId,
            appointment_type_name: typeName,
            procedure_external_id: null,
            procedure_name: null,
            appointment_status: status,
            patient_status: patStatus,
            count: existing?.count || 0,
            unique_patients: existing?.unique_patients || 0,
          });
        }
      }

      return allStatusCombinations;
    },
  });

  const getTypePatientKey = (typeExtId: number | null, patientStatus: string | null): string => {
    return `${typeExtId || 'null'}|${patientStatus || 'null'}`;
  };

  // Group state combinations by appointment type and patient status
  const groupedByType = React.useMemo(() => {
    if (!stateCombinations) return [];
    
    const groups = new Map<string, {
      typeExtId: number | null;
      typeName: string;
      patientStatus: string | null;
      statuses: StateCombination[];
    }>();

    stateCombinations.forEach((combo) => {
      const groupKey = getTypePatientKey(combo.appointment_type_external_id, combo.patient_status);
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          typeExtId: combo.appointment_type_external_id,
          typeName: combo.appointment_type_name,
          patientStatus: combo.patient_status,
          statuses: [],
        });
      }
      groups.get(groupKey)!.statuses.push(combo);
    });

    return Array.from(groups.values()).sort((a, b) => {
      const nameA = a.typeName || '';
      const nameB = b.typeName || '';
      return nameA.localeCompare(nameB);
    });
  }, [stateCombinations]);

  const coverageBoard = React.useMemo(() => {
    const counts = new Map<number, number>();
    (stateCombinations || []).forEach((combo) => {
      if (combo.appointment_type_external_id == null) return;
      counts.set(
        combo.appointment_type_external_id,
        (counts.get(combo.appointment_type_external_id) || 0) + (combo.count || 0)
      );
    });
    const mappingsByType = new Map<number, typeof existingMappings>();
    (existingMappings || []).forEach((m: any) => {
      const typeId = Number(m.appointment_type_external_id);
      if (!Number.isFinite(typeId)) return;
      const list = mappingsByType.get(typeId) || [];
      list.push(m);
      mappingsByType.set(typeId, list);
    });
    const rows: CoverageRow[] = (catalogTypes || []).map((type: any) => {
      const maps = mappingsByType.get(Number(type.external_id)) || [];
      const hasCreate = maps.some((m: any) => !m.skip_opportunity && m.opportunity_status && m.opportunity_status !== 'none');
      const needsReview = type.sync_status === 'needs_review' || /needs review\)\s*$/i.test(String(type.name || ''));
      const status = needsReview ? 'red' : hasCreate ? 'green' : 'yellow';
      return {
        externalId: type.external_id,
        name: type.name,
        appointmentCount: counts.get(Number(type.external_id)) || 0,
        status,
        reason: needsReview
          ? 'Needs review — 4D sent no name or this type is still unclassified'
          : hasCreate
            ? 'Mapped and can create or update a card'
            : maps.length
              ? 'Skip-only — no card will be created'
              : 'In middleware, not mapped yet',
      };
    });
    const known = new Set(rows.map((r) => Number(r.externalId)));
    (typeAlerts || []).forEach((alert: any) => {
      const extId = Number(alert.appointment_type_external_id);
      if (!Number.isFinite(extId) || known.has(extId)) return;
      if (alert.delivery_status === 'resolved') return;
      rows.push({
        externalId: extId,
        name: alert.appointment_type_name || `Type ${extId} (missing from middleware)`,
        appointmentCount: counts.get(extId) || 0,
        status: 'red',
        reason: 'Seen in 4D, not ingested into middleware',
      });
    });
    const order = { red: 0, yellow: 1, green: 2 };
    rows.sort((a, b) => order[a.status as keyof typeof order] - order[b.status as keyof typeof order] || a.externalId - b.externalId);
    return {
      rows,
      red: rows.filter((r) => r.status === 'red').length,
      yellow: rows.filter((r) => r.status === 'yellow').length,
      green: rows.filter((r) => r.status === 'green').length,
    };
  }, [catalogTypes, existingMappings, stateCombinations, typeAlerts]);

  const refreshPipelines = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const auth = resolveGhlAuth(zoneDMConfig as any);
      if (!auth) {
        toast.error(GHL_AUTH_MISSING_MESSAGE);
        return;
      }
      const api = initializeZoneDMApi(toZoneDmCredentials(auth));
      const pipelines = await api.listPipelines();
      const locationId = auth.locationId;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const list = Array.isArray((pipelines as any)?.pipelines)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (pipelines as any).pipelines
        : (Array.isArray(pipelines) ? pipelines : []);
      if (!Array.isArray(list) || list.length === 0) {
        toast('No pipelines returned');
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows = list.map((p: any) => ({
        user_id: session.user.id,
        location_id: locationId,
        hl_pipeline_id: p?.id || p?.pipelineId,
        name: p?.name || '',
        stages: p?.stages || [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })).filter((r: any) => r.user_id && r.hl_pipeline_id);
      if (rows.length) {
        await supabase.from('hl_pipelines').upsert(rows, { onConflict: 'user_id,location_id,hl_pipeline_id' });
        await queryClient.invalidateQueries({ queryKey: ['hl_pipelines'] });
        toast.success(`Pipelines refreshed (${rows.length})`);
        
        // Check if user needs default mappings and offer to create them
        const needsMappings = await checkNeedsDefaultMappings(session.user.id);
        if (needsMappings) {
          setShowAutoMapPrompt(true);
        }
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to refresh pipelines';
      toast.error(errorMessage);
    }
  };

  const handleGenerateDefaultMappings = async (overwrite: boolean = false) => {
    try {
      setIsGeneratingDefaults(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      if (overwrite) {
        const confirmed = window.confirm(
          'Auto-Map Defaults replaces your current pipeline mappings. Review the generated opportunity matching choices before saving. Continue?'
        );
        if (!confirmed) return;
      }

      const result = await generateDefaultPipelineMappings(session.user.id, undefined, overwrite);
      
      if (result.success) {
        toast.success(`Created ${result.mappingsCreated} default mappings based on appointment types and pipeline stages`);
        await queryClient.invalidateQueries({ queryKey: ['appointment_pipeline_mappings'] });
        setShowAutoMapPrompt(false);
      } else {
        toast.error(result.error || 'Failed to generate default mappings');
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to generate default mappings';
      toast.error(errorMessage);
    } finally {
      setIsGeneratingDefaults(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getMappingKey = (item: StateCombination | PipelineMapping | any): string => {
    return `${item.appointment_type_external_id || ''}|${item.procedure_external_id || ''}|${item.appointment_status || ''}|${item.patient_status || ''}`;
  };

  const getPipelineStages = (pipelineId: string | null): Array<{ id: string; name: string }> => {
    if (!pipelineId || !hlPipelines) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pipeline = hlPipelines.find((p: any) => p.hl_pipeline_id === pipelineId);
    if (!pipeline || !pipeline.stages) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stagesArray: any[] = Array.isArray(pipeline.stages) ? pipeline.stages : [];
    return stagesArray
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((stage: any) => {
        const id: string = stage?.id || stage?.stageId || stage?.pipelineStageId || '';
        const name: string =
          stage?.name ||
          stage?.label ||
          stage?.title ||
          stage?.displayName ||
          '[Unnamed Stage]';
        return { id, name };
      })
      .filter((stage) => stage.id);
  };

  const onChangeMapping = (
    key: string,
    item: StateCombination,
    field: 'pipeline' | 'stage' | 'oppStatus' | 'skip' | 'priority' | 'createNew' | 'dateMode' | 'stageOrder',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ) => {
    const current = editableMappings[key] || {
      appointment_type_external_id: item.appointment_type_external_id,
      procedure_external_id: item.procedure_external_id,
      appointment_status: item.appointment_status,
      patient_status: item.patient_status,
      hl_pipeline_id: null,
      hl_pipeline_stage_id: null,
      opportunity_status: null,
      skip_opportunity: false,
      create_new_opportunity: false,
      date_field_mode: 'auto' as DateFieldMode,
      stage_order: 0,
      priority_order: 0,
    };

    if (field === 'pipeline') {
      setSelectedPipeline((prev) => ({ ...prev, [key]: value }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, hl_pipeline_id: value, hl_pipeline_stage_id: null },
      }));
      // Clear stage when pipeline changes
      setSelectedStage((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else if (field === 'stage') {
      setSelectedStage((prev) => ({ ...prev, [key]: value }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, hl_pipeline_stage_id: value },
      }));
    } else if (field === 'oppStatus') {
      setSelectedOppStatus((prev) => ({ ...prev, [key]: value }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, opportunity_status: value === 'none' ? null : value },
      }));
    } else if (field === 'skip') {
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, skip_opportunity: value },
      }));
    } else if (field === 'priority') {
      setPriorityOrders((prev) => ({ ...prev, [key]: value }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, priority_order: value },
      }));
    } else if (field === 'createNew') {
      setCreateNewOpp((prev) => ({ ...prev, [key]: value }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, create_new_opportunity: value },
      }));
    } else if (field === 'dateMode') {
      const normalized = normalizeDateFieldMode(value);
      setDateFieldModes((prev) => ({ ...prev, [key]: normalized }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, date_field_mode: normalized },
      }));
    } else if (field === 'stageOrder') {
      setStageOrders((prev) => ({ ...prev, [key]: value }));
      setEditableMappings((prev) => ({
        ...prev,
        [key]: { ...current, stage_order: value },
      }));
    }
  };

  const onSaveMappings = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    
    setIsSaving(true);

    const rows = Object.entries(editableMappings)
      .filter(([, mapping]) => {
        const typeName = groupedByType.find(
          (group) => group.typeExtId === mapping.appointment_type_external_id
        )?.typeName;
        if (isProtectedVisitingSurgeonType(
          typeName,
          mapping.appointment_type_external_id,
          session.user.id,
        )) return false;
        // Include if pipeline/stage is set, or if skip_opportunity is true
        return (mapping.hl_pipeline_id && mapping.hl_pipeline_stage_id) || mapping.skip_opportunity;
      })
      .map(([, mapping]) => {
        // When skip_opportunity is true, use NULL for pipeline/stage
        // Otherwise, use the provided values or empty string (which will be converted to NULL)
        const pipelineId = mapping.skip_opportunity 
          ? null 
          : (mapping.hl_pipeline_id && mapping.hl_pipeline_id.trim() !== '' ? mapping.hl_pipeline_id : null);
        const stageId = mapping.skip_opportunity 
          ? null 
          : (mapping.hl_pipeline_stage_id && mapping.hl_pipeline_stage_id.trim() !== '' ? mapping.hl_pipeline_stage_id : null);
        
        return {
          user_id: session.user.id,
          appointment_type_external_id: mapping.appointment_type_external_id,
          procedure_external_id: mapping.procedure_external_id,
          appointment_status: mapping.appointment_status,
          patient_status: mapping.patient_status,
          hl_pipeline_id: pipelineId,
          hl_pipeline_stage_id: stageId,
          opportunity_status: mapping.opportunity_status,
          skip_opportunity: mapping.skip_opportunity,
          create_new_opportunity: mapping.create_new_opportunity || false,
          date_field_mode: normalizeDateFieldMode(mapping.date_field_mode),
          stage_order: mapping.stage_order || 0,
          priority_order: mapping.priority_order || 0,
        };
      });

    if (rows.length === 0) {
      toast.error('No mappings to save');
      return;
    }

    try {
      // Use upsert to avoid data loss - delete only the specific combinations we're updating
      // First, collect unique keys to delete
      const keysToDelete = new Map<string, typeof rows[0]>();
      rows.forEach((r) => {
        const key = `${r.appointment_type_external_id}|${r.procedure_external_id}|${r.appointment_status}|${r.patient_status || 'null'}`;
        keysToDelete.set(key, r);
      });

      // Delete only the combinations we're updating (in smaller batches to avoid timeouts)
      const deleteBatchSize = 50;
      const deleteKeys = Array.from(keysToDelete.values());
      
      for (let i = 0; i < deleteKeys.length; i += deleteBatchSize) {
        const batch = deleteKeys.slice(i, i + deleteBatchSize);
        
        for (const key of batch) {
          let deleteQuery = supabase
            .from('appointment_pipeline_mappings')
            .delete()
            .eq('user_id', key.user_id)
            .eq('appointment_type_external_id', key.appointment_type_external_id)
            .eq('appointment_status', key.appointment_status);
          
          // Handle null patient_status
          if (key.patient_status === null || key.patient_status === undefined) {
            deleteQuery = deleteQuery.is('patient_status', null);
          } else {
            deleteQuery = deleteQuery.eq('patient_status', key.patient_status);
          }
          
          if (key.procedure_external_id === null || key.procedure_external_id === undefined) {
            deleteQuery = deleteQuery.is('procedure_external_id', null);
          } else {
            deleteQuery = deleteQuery.eq('procedure_external_id', key.procedure_external_id);
          }

          await deleteQuery;
        }
      }

      // Insert new mappings in batches
      const insertBatchSize = 100;
      let inserted = 0;
      
      for (let i = 0; i < rows.length; i += insertBatchSize) {
        const batch = rows.slice(i, i + insertBatchSize);
        const { error: insertError } = await supabase
          .from('appointment_pipeline_mappings')
          .insert(batch);
        
        if (insertError) {
          // If insert fails, try to restore by re-running the script
          console.error('Insert error:', insertError);
          throw new Error(`Failed to save mappings: ${insertError.message}. Please refresh the page and try again, or run the restore script.`);
        }
        
        inserted += batch.length;
      }

      await queryClient.invalidateQueries({ queryKey: ['appointment_pipeline_mappings'] });
      toast.success(`Saved ${inserted} mapping(s)`);
    } catch (e: unknown) {
      console.error('Save error:', e);
      const errorMessage = e instanceof Error ? e.message : 'Failed to save mappings. Data may have been lost - please refresh and try again.';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const formatStatusLabel = (status: string): string => {
    // Convert snake_case to Title Case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const toggleStatusSelection = (typeExtId: number | null, patientStatus: string | null, status: string) => {
    const typePatientKey = getTypePatientKey(typeExtId, patientStatus);
    const current = selectedStatuses[typePatientKey] || [];
    const newSelection = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    
    setSelectedStatuses((prev) => ({
      ...prev,
      [typePatientKey]: newSelection,
    }));

    // If deselecting, clear the mapping for that status
    if (current.includes(status)) {
      const key = getMappingKey({
        appointment_type_external_id: typeExtId,
        procedure_external_id: null,
        appointment_status: status,
        patient_status: patientStatus,
      });
      setEditableMappings((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSelectedPipeline((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSelectedStage((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSelectedOppStatus((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPriorityOrders((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setDateFieldModes((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };


  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Opportunity Mapping</h1>
          <p className="mt-1 text-sm text-gray-500">
            Map appointment/patient state combinations to ZoneDM pipeline stages
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as 'current_year' | 'all')}
            className="border border-gray-300 rounded-md text-sm px-3 py-2"
          >
            <option value="current_year">Current Year</option>
            <option value="all">All Time</option>
          </select>
          <button
            onClick={refreshPipelines}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Pipelines
          </button>
          <button
            onClick={() => handleGenerateDefaultMappings(true)}
            disabled={isGeneratingDefaults}
            className="px-4 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded flex items-center gap-2 disabled:opacity-50"
          >
            {isGeneratingDefaults ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4" />
            )}
            Auto-Map Defaults
          </button>
        </div>
      </div>

      {/* Auto-Map Prompt */}
      {showAutoMapPrompt && (
        <Card className="mb-6 border-2 border-blue-500 bg-blue-50">
          <div className="px-6 py-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Wand2 className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900">Create Default Pipeline Mappings?</h3>
                <p className="text-blue-800 mt-1">
                  We detected you have pipelines but no mappings configured. Would you like to automatically generate 
                  intelligent default mappings based on your appointment types and pipeline stages?
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  The system will analyze your pipeline stage names (e.g., "Consult Booked", "Surgery Completed") 
                  and appointment types to create sensible defaults. You can always edit them later.
                </p>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => handleGenerateDefaultMappings(true)}
                    disabled={isGeneratingDefaults}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isGeneratingDefaults ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Generate Default Mappings
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowAutoMapPrompt(false)}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Skip for Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* How It Works - Explanatory Section */}
      <Card className="mb-6">
        <div className="px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">How Opportunity Matching Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">🔗 Quote-Based Matching</h4>
              <p className="text-blue-800">
                For appointments with a linked quote (Surgery, etc.), the system matches opportunities by <strong>quote ID</strong>. 
                This ensures each patient journey (quote) has its own opportunity, even if the patient has multiple procedures.
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">✨ Opportunity Matching</h4>
              <p className="text-green-800">
                <strong>Reuse open; create if none</strong> is recommended when web forms or ads already create inquiry cards.{' '}
                Use <strong>Always create new</strong> only when parallel journeys for the same contact are intentional.
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-2">📊 Stage Order</h4>
              <p className="text-purple-800">
                Set the <strong>order of stages</strong> in the patient journey (1=first, 2=second, etc.). 
                This helps visualize the flow and ensures opportunities move in the correct sequence.
              </p>
            </div>
          </div>
        </div>
      </Card>

      <Card className="mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Appointment type coverage</h3>
          <p className="mt-1 text-sm text-gray-600">
            Every 4D type should be ingested and classified. Red types can still create the wrong card. Yellow types are
            skip-by-default until you map them.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-800">
              {coverageBoard.red} needs review
            </span>
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-1 font-medium text-yellow-800">
              {coverageBoard.yellow} skip / unmapped
            </span>
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 font-medium text-green-800">
              {coverageBoard.green} mapped
            </span>
          </div>
        </div>
        <div className="max-h-72 overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-6 py-2">Status</th>
                <th className="px-6 py-2">4D type</th>
                <th className="px-6 py-2">Name</th>
                <th className="px-6 py-2">Appointments</th>
                <th className="px-6 py-2">Why</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coverageBoard.rows.map((row) => (
                <tr key={row.externalId}>
                  <td className="px-6 py-2">
                    <span
                      className={
                        row.status === 'red'
                          ? 'font-semibold text-red-700'
                          : row.status === 'yellow'
                            ? 'font-semibold text-yellow-700'
                            : 'font-semibold text-green-700'
                      }
                    >
                      {row.status === 'red' ? 'Red' : row.status === 'yellow' ? 'Yellow' : 'Green'}
                    </span>
                  </td>
                  <td className="px-6 py-2 text-gray-900">{row.externalId}</td>
                  <td className="px-6 py-2 text-gray-900">{row.name}</td>
                  <td className="px-6 py-2 text-gray-700">{row.appointmentCount}</td>
                  <td className="px-6 py-2 text-gray-600">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 group">
            {collapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
            )}
            <h3 className="text-lg font-medium text-gray-900">State Combinations</h3>
          </button>
          <div className="text-sm text-gray-500 flex items-center gap-4">
            <span>Total: {stateCombinations?.length || 0}</span>
            <span>Pipelines: {hlPipelines?.length || 0}</span>
          </div>
        </div>
        {!collapsed && (
          <>
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="px-6 py-8 text-center text-sm text-gray-500">Loading...</div>
              ) : groupedByType.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {groupedByType.map((group) => {
                    const typePatientKey = getTypePatientKey(group.typeExtId, group.patientStatus);
                    const selectedStatusList = selectedStatuses[typePatientKey] || [];
                    const isProtectedType = isProtectedVisitingSurgeonType(
                      group.typeName,
                      group.typeExtId,
                      currentUserId,
                    );
                    
                    return (
                      <div key={typePatientKey} className="p-6">
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">
                            {group.typeName}
                            {isProtectedType && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                                System protected — never sent to GHL
                              </span>
                            )}
                            {group.patientStatus && (
                              <span className="text-gray-500 font-normal ml-2">| Patient: {group.patientStatus}</span>
                            )}
                            {!group.patientStatus && (
                              <span className="text-gray-500 font-normal ml-2">| No Patient Status</span>
                            )}
                          </h4>
                          <p className={`text-xs mb-3 ${isProtectedType ? 'text-red-700' : 'text-gray-500'}`}>
                            {isProtectedType
                              ? 'Visiting Surgeon appointments are permanently blocked from GHL calendar events and opportunity cards. This cannot be overridden by mapping configuration.'
                              : 'Select statuses to map to pipeline stages:'}
                          </p>
                          
                          {/* Status selection checkboxes */}
                          {!isProtectedType && <div className="flex flex-wrap gap-3 mb-4">
                            {ALL_APPOINTMENT_STATUSES.map((status) => {
                              const statusCombo = group.statuses.find((s) => s.appointment_status === status);
                              const isSelected = selectedStatusList.includes(status);
                              const count = statusCombo?.count || 0;
                              const patients = statusCombo?.unique_patients || 0;
                              
                              return (
                                <label
                                  key={status}
                                  className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-gray-50"
                                  style={{
                                    backgroundColor: isSelected ? '#eef2ff' : 'white',
                                    borderColor: isSelected ? '#6366f1' : '#d1d5db',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleStatusSelection(group.typeExtId, group.patientStatus, status)}
                                    className="rounded"
                                  />
                                  <span className="text-sm font-medium">{formatStatusLabel(status)}</span>
                                  {count > 0 && (
                                    <span className="text-xs text-gray-500">
                                      ({count.toLocaleString()} appts, {patients} patients)
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>}
                        </div>

                        {/* Mapping controls for selected statuses */}
                        {!isProtectedType && selectedStatusList.length > 0 && (
                          <div className="mt-4 border-t border-gray-200 pt-4">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Skip
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Choose whether to reuse an open opportunity for this contact and pipeline or always create another card">
                                    Opportunity Matching
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Controls whether Consultation Date or Procedure Date is set from this mapping">
                                    Date Field
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Pipeline
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Stage
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Order of this stage in the pipeline flow (1=first, 2=second, etc.)">
                                    Order
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Opp Status
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Priority
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {selectedStatusList.map((status) => {
                                  const statusCombo = group.statuses.find((s) => s.appointment_status === status);
                                  if (!statusCombo) return null;
                                  
                                  const key = getMappingKey(statusCombo);
                                  const mapping = editableMappings[key];
                                  const skipOpp = mapping?.skip_opportunity || false;
                                  const createNew = createNewOpp[key] ?? mapping?.create_new_opportunity ?? false;
                                  const dateMode = dateFieldModes[key] ?? normalizeDateFieldMode(mapping?.date_field_mode);
                                  const pipelineId = selectedPipeline[key] || mapping?.hl_pipeline_id || '';
                                  const stageId = selectedStage[key] || mapping?.hl_pipeline_stage_id || '';
                                  const stageOrder = stageOrders[key] ?? mapping?.stage_order ?? 0;
                                  const oppStatus = selectedOppStatus[key] || mapping?.opportunity_status || 'none';
                                  const priority = priorityOrders[key] ?? mapping?.priority_order ?? 0;
                                  const stages = getPipelineStages(pipelineId);

                                  return (
                                    <tr key={key}>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <span className="text-sm font-medium text-gray-900">{formatStatusLabel(status)}</span>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <label className="flex items-center gap-2">
                                          <input
                                            type="checkbox"
                                            checked={skipOpp}
                                            onChange={(e) => onChangeMapping(key, statusCombo, 'skip', e.target.checked)}
                                            className="rounded"
                                          />
                                          <span className="text-xs text-gray-500">Skip</span>
                                        </label>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap min-w-[260px]">
                                        {!skipOpp && (
                                          <div>
                                            <select
                                              aria-label={`Opportunity matching for ${formatStatusLabel(status)}`}
                                              className={`border rounded-md text-sm px-2 py-1 w-full ${
                                                createNew
                                                  ? 'border-amber-400 bg-amber-50 text-amber-900'
                                                  : 'border-green-300 bg-green-50 text-green-900'
                                              }`}
                                              value={createNew ? 'always_new' : 'reuse_open'}
                                              onChange={(e) =>
                                                onChangeMapping(
                                                  key,
                                                  statusCombo,
                                                  'createNew',
                                                  e.target.value === 'always_new'
                                                )
                                              }
                                            >
                                              <option value="reuse_open">Reuse open; create if none</option>
                                              <option value="always_new">Always create new</option>
                                            </select>
                                            <p className={`mt-1 text-xs ${createNew ? 'text-amber-700' : 'text-green-700'}`}>
                                              {createNew
                                                ? 'Creates another card even when this contact already has an open card.'
                                                : 'Advances an existing open card; completed journeys remain separate.'}
                                            </p>
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {!skipOpp && (
                                          <select
                                            className="border border-gray-300 rounded-md text-sm px-2 py-1 max-w-[190px]"
                                            value={dateMode}
                                            onChange={(e) => onChangeMapping(key, statusCombo, 'dateMode', e.target.value)}
                                          >
                                            {DATE_FIELD_MODE_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {!skipOpp && (
                                          <select
                                            className="border border-gray-300 rounded-md text-sm px-2 py-1 max-w-[200px]"
                                            value={pipelineId}
                                            onChange={(e) => onChangeMapping(key, statusCombo, 'pipeline', e.target.value)}
                                          >
                                            <option value="">Not Mapped</option>
                                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                            {(hlPipelines || []).map((p: any) => (
                                              <option key={p.hl_pipeline_id} value={p.hl_pipeline_id}>
                                                {p.name || p.hl_pipeline_id}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {!skipOpp && (
                                          <select
                                            className="border border-gray-300 rounded-md text-sm px-2 py-1 max-w-[200px]"
                                            value={stageId}
                                            onChange={(e) => onChangeMapping(key, statusCombo, 'stage', e.target.value)}
                                            disabled={!pipelineId}
                                          >
                                            <option value="">Select Stage</option>
                                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                                            {stages.map((s: any) => (
                                              <option key={s.id || s.stageId} value={s.id || s.stageId}>
                                                {s.name || s.stageName || s.id}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {!skipOpp && (
                                          <input
                                            type="number"
                                            className="border border-gray-300 rounded-md text-sm px-2 py-1 w-16"
                                            value={stageOrder}
                                            onChange={(e) => onChangeMapping(key, statusCombo, 'stageOrder', parseInt(e.target.value) || 0)}
                                            min="0"
                                            title="Order of this stage in the pipeline flow (1=first, 2=second, etc.)"
                                          />
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {!skipOpp && (
                                          <select
                                            className="border border-gray-300 rounded-md text-sm px-2 py-1 max-w-[150px]"
                                            value={oppStatus}
                                            onChange={(e) => onChangeMapping(key, statusCombo, 'oppStatus', e.target.value)}
                                          >
                                            <option value="none">No Opportunity</option>
                                            <option value="open">Open</option>
                                            <option value="won">Won</option>
                                            <option value="lost">Lost</option>
                                            <option value="closed">Closed</option>
                                          </select>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        <input
                                          type="number"
                                          className="border border-gray-300 rounded-md text-sm px-2 py-1 w-20"
                                          value={priority}
                                          onChange={(e) => onChangeMapping(key, statusCombo, 'priority', parseInt(e.target.value) || 0)}
                                          min="0"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-sm text-gray-500">
                  No appointment types found
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={onSaveMappings}
                disabled={isSaving}
                className="bg-indigo-600 text-white px-6 py-2 rounded shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Mappings'
                )}
              </button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

